import { DispatchCard } from 'src/entities/dispatch';
import { VehicleRowVM } from 'src/pages/ConvenienceDispatches/vehicleRowVM';

/**
 * 配車済みの降地か。展開条件・stop 抽出・容量集計のすべてでこれを使う。
 * status: 'template' かつ isActive: true（D&D 直後の未反映状態・Presenter.tsx:414-415）を
 * 除くため両方を見る。/map は D&D しないので、この状態のカードは表示対象にしない。
 * LoadingCard は status も isActive も持たないので type で先に落ちる。
 */
export const isAssignedDelivery = (card: DispatchCard): boolean =>
  card.type === 'delivery' && card.status === 'matched' && card.isActive;

/**
 * その行に配車済みの降地があるか。空回転（再積み込み拠点だけの placeholder 行・
 * rotationEditing.ts:60-70）を展開対象から外すために使う。
 * 行単位とカード単位で判定を分けているのは、「積地だけの行」と
 * 「積地＋無効な降地の行」を区別する必要があるため。
 */
export const hasAssignedDelivery = (cards: readonly DispatchCard[]): boolean =>
  cards.some(isAssignedDelivery);

/**
 * その行に積の operation があるか。宵積みの 1 日目のように、降地が翌日の勤務にあって
 * 積だけの勤務でも、案件は配車済みなので「計画あり」に数える (log_web_issues#343)。
 * 旧マップ (/plans) も operation に含まれる案件の数で判定しており、積も数える
 * (OperationsWithStatus.model.ts:100)。
 *
 * Why not 表示用カード (DispatchCard) の type === 'loading' で見る: 空回転の「再積み込み拠点」
 * (rotationEditing.ts:60-70) と、カード配車で D&D した直後の積地も同じ loading カードで、
 * どちらも配車済みではない。サーバの operation を持つ元データ (VehicleRowVM) で見れば、
 * placeholder は operations が空、D&D の積地は元データに入らないので両方とも外れる。
 */
export const hasAssignedLoading = (source: Pick<VehicleRowVM, 'cards' | 'isPlaceholderRow'>): boolean =>
  !source.isPlaceholderRow
  && source.cards.some((card) => card.kind === 'loading' && card.operations.length > 0);

/**
 * その行に「自動配車で割り当てる中身」があるか。
 *
 * 配車済み (matched) に加えて、D&D で置いた直後の未反映カード (template) も数える。
 * カード配車は置いたカードを自動配車で保存する流れがあり、サーバの配車だけを見ると
 * 「割り当てるものが無い」と誤判定して保存できなくなる。
 * 展開条件・容量集計は従来どおり hasAssignedDelivery（matched だけ）を使う。
 */
export const hasDispatchableCard = (cards: readonly DispatchCard[]): boolean =>
  cards.some((card) => isAssignedDelivery(card)
    || (card.type === 'delivery' && card.status === 'template' && card.isActive));
